import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { toast } from 'react-toastify';
import api from '../utils/axiosConfig';
import { useAuth } from '../context/AuthContext';
import '../styles/DashboardPage.css';

// ── Constants ─────────────────────────────────────────────────────────────────
const HOUR_HEIGHT = 80; // px per hour
const MINUTE_HEIGHT = HOUR_HEIGHT / 60;
const HOURS = Array.from({ length: 24 }, (_, i) => i);

const DAY_NAMES_AR = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
const MONTH_NAMES_AR = [
  'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
  'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر',
];

// ── Helpers ───────────────────────────────────────────────────────────────────
function formatDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function parseCalendarDate(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return null;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return formatDate(date) === value ? date : null;
}

function formatDateArabic(date) {
  return `${DAY_NAMES_AR[date.getDay()]}، ${date.getDate()} ${MONTH_NAMES_AR[date.getMonth()]} ${date.getFullYear()}`;
}

function timeToMinutes(timeStr) {
  if (!timeStr) return 0;
  const [h, m] = timeStr.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

function formatHourLabel(h) {
  if (h === 0) return '12 ص';
  if (h < 12) return `${h} ص`;
  if (h === 12) return '12 م';
  return `${h - 12} م`;
}

function formatCreatedAt(isoStr) {
  if (!isoStr) return '—';
  try {
    // Backend stores UTC without 'Z' — append it so JS parses as UTC, then converts to local time
    const utcStr = isoStr.endsWith('Z') ? isoStr : isoStr + 'Z';
    const d = new Date(utcStr);
    const date = d.toLocaleDateString('en-GB');
    const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    return `${date} — ${time}`;
  } catch {
    return isoStr;
  }
}

function currentTimeHHMM() {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
}

function filterUpcomingBookings(bookings, dateStr) {
  const todayStr = formatDate(new Date());
  if (dateStr !== todayStr) return bookings;
  const nowTime = currentTimeHHMM();
  return bookings.filter((b) => (b.time || '') >= nowTime);
}

function addMinutes(timeStr, minutes) {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':').map(Number);
  const total = (h || 0) * 60 + (m || 0) + minutes;
  const endH = Math.floor(total / 60) % 24;
  const endM = total % 60;
  return `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
}

export function minutesToHHMM(totalMinutes) {
  const clamped = Math.max(0, Math.min((24 * 60) - 5, totalMinutes));
  const snapped = Math.round(clamped / 5) * 5;
  const h = Math.floor(snapped / 60);
  const m = snapped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function buildDefaultBulkSmsMessage(salonTitle) {
  return `تذكير: لديك موعد اليوم الساعة {time} في ${salonTitle || 'الصالون'}. نراك قريباً! — ادواري`;
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function DashboardPage() {
  const { account } = useAuth();

  const [selectedDate, setSelectedDate] = useState(
    () => parseCalendarDate(new URLSearchParams(window.location.search).get('date')) || new Date()
  );
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [closing, setClosing] = useState(false);
  const [isClosed, setIsClosed] = useState(false);

  // Current time indicator (updates every minute)
  const [nowMinutes, setNowMinutes] = useState(() => {
    const n = new Date();
    return n.getHours() * 60 + n.getMinutes();
  });

  // Booking form
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [reservationTypes, setReservationTypes] = useState([]);
  const [bookingPhone, setBookingPhone] = useState('');
  const [bookingName, setBookingName] = useState('');
  const [bookingTime, setBookingTime] = useState('');
  const [bookingTypeId, setBookingTypeId] = useState('');
  const [bookingSaving, setBookingSaving] = useState(false);
  const [loadingTimes, setLoadingTimes] = useState(false);
  const [preSelectedTime, setPreSelectedTime] = useState('');
  const [lookingUpPhone, setLookingUpPhone] = useState(false);

  // Detail popup
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [editTime, setEditTime] = useState('');
  const [editTimes, setEditTimes] = useState([]);
  const [loadingEditTimes, setLoadingEditTimes] = useState(false);
  const [editSaving, setEditSaving] = useState(false);

  // Allowed list prompt
  const [allowedPrompt, setAllowedPrompt] = useState(null);
  const [addingToList, setAddingToList] = useState(false);

  // Bulk SMS
  const [showBulkSms, setShowBulkSms] = useState(false);
  const [bulkSmsMessage, setBulkSmsMessage] = useState('');
  const [bulkSmsSending, setBulkSmsSending] = useState(false);

  // Hover time display
  const [hoverTime, setHoverTime] = useState('');

  // Custom cancel confirmation dialog
  // { booking, blockEl, wrapEl } — blockEl/wrapEl are set when triggered by drag (for snap-back)
  const [pendingCancel, setPendingCancel] = useState(null);
  // { booking, newTime, blockEl, wrapEl }
  const [pendingMove, setPendingMove] = useState(null);

  // Refs
  const timelineRef = useRef(null);
  const eventsRef = useRef(null);
  const blockElsRef = useRef({});        // DOM refs for each booking block inner div
  const dragRef = useRef({
    active: false, id: null, startX: 0, startY: 0, dx: 0, dy: 0, axis: null, originTop: 0,
  });
  const dragDidMoveRef = useRef(false);  // suppress click after drag
  const bookingsRef = useRef(bookings);  // always-current bookings list (avoids stale closure)
  const setPendingCancelRef = useRef(setPendingCancel);
  const setPendingMoveRef = useRef(setPendingMove);

  const DRAG_THRESHOLD = 90; // px left to trigger cancel
  const MOVE_THRESHOLD = 18; // px vertical to treat as time move

  // Keep refs in sync on every render
  bookingsRef.current = bookings;
  setPendingCancelRef.current = setPendingCancel;
  setPendingMoveRef.current = setPendingMove;

  // ── Calendar settings (for working hours shading) ────────────────────────
  const [calendarSettings, setCalendarSettings] = useState(null);

  useEffect(() => {
    api.get('/backoffice/calendar-settings')
      .then(res => { if (res.data.success) setCalendarSettings(res.data.settings); })
      .catch(() => {});
  }, []);

  // ── Load reservation types on mount (needed for block heights) ────────────
  useEffect(() => {
    api.get('/backoffice/reservation-types')
      .then(res => { if (res.data.success) setReservationTypes(res.data.reservation_types); })
      .catch(() => {});
  }, []);

  // ── Real-time clock update ────────────────────────────────────────────────
  useEffect(() => {
    const timer = setInterval(() => {
      const n = new Date();
      setNowMinutes(n.getHours() * 60 + n.getMinutes());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  // ── Fetch bookings ────────────────────────────────────────────────────────
  const fetchBookings = useCallback(async (date) => {
    setLoading(true);
    try {
      const { data } = await api.get('/backoffice/bookings/by-date', {
        params: { date: formatDate(date) },
      });
      if (data.success) {
        setBookings(data.bookings || []);
        setIsClosed(!!data.is_closed);
      }
    } catch {
      setBookings([]);
      setIsClosed(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBookings(selectedDate);
  }, [selectedDate, fetchBookings]);

  // ── Centre current time (or 9 AM) once the timeline finishes loading ────────
  useEffect(() => {
    if (loading) return; // wait until timeline is rendered with real height
    const el = timelineRef.current;
    if (!el) return;
    const isToday = formatDate(selectedDate) === formatDate(new Date());
    const targetMinute = isToday ? nowMinutes : 9 * 60;
    const targetPx = targetMinute * MINUTE_HEIGHT;
    // Use actual container height; fall back to window estimate if not yet laid out
    const visibleHeight = el.clientHeight || window.innerHeight - 200;
    el.scrollTop = Math.max(0, targetPx - visibleHeight / 2);
  }, [loading, selectedDate]); // nowMinutes intentionally omitted to avoid re-scroll on clock tick

  // ── Date navigation ───────────────────────────────────────────────────────
  const goDay = (offset) => {
    setSelectedDate(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() + offset);
      return d;
    });
  };

  const goToday = () => setSelectedDate(new Date());

  const handleDateInput = (val) => {
    if (!val) return;
    const [y, m, d] = val.split('-').map(Number);
    if (y && m && d) setSelectedDate(new Date(y, m - 1, d));
  };

  const isToday = formatDate(selectedDate) === formatDate(new Date());
  const selectedDateStr = formatDate(selectedDate);

  const smsBookings = useMemo(
    () => filterUpcomingBookings(bookings, selectedDateStr),
    [bookings, selectedDateStr],
  );

  // Get duration for a booking (used for block height)
  const getBookingDuration = useCallback((booking) => {
    const rt = reservationTypes.find(r => r.id === booking.reservation_type_id);
    return rt?.duration_minutes || 60;
  }, [reservationTypes]);

  // ── Action handlers ───────────────────────────────────────────────────────

  const handleCloseDay = async () => {
    if (!window.confirm(
      `هل أنت متأكد من إغلاق يوم ${formatDateArabic(selectedDate)}؟\nسيتم تحديث عداد الحجوزات لجميع العملاء.`
    )) return;
    setClosing(true);
    try {
      const { data } = await api.post('/backoffice/close-day', { date: formatDate(selectedDate) });
      if (data.success) { toast.success(data.message); setIsClosed(true); }
      else toast.error(data.message);
    } catch { toast.error('حدث خطأ في إغلاق اليوم'); }
    finally { setClosing(false); }
  };

  // ── Low-level delete (called after confirmation or for past bookings) ────────
  const doDeleteBooking = async (bookingId, sendSms) => {
    try {
      const params = sendSms ? {} : { skip_sms: 'true' };
      const { data } = await api.delete(`/backoffice/bookings/${bookingId}`, { params });
      if (data.success) {
        toast.success(sendSms ? (data.message || 'تم إلغاء الحجز') : 'تم حذف الحجز');
        if (sendSms && data.sms_sent) toast.info('تم إرسال SMS للعميل');
        fetchBookings(selectedDate);
      } else toast.error(data.message);
    } catch (err) { toast.error(err.response?.data?.message || 'حدث خطأ'); }
  };

  const doRescheduleBooking = async (booking, newTime) => {
    try {
      const { data } = await api.put(`/backoffice/bookings/${booking.id}/reschedule`, {
        date: booking.date,
        time: newTime,
      });
      if (data.success) {
        toast.success(data.message || 'تم تحديث الموعد');
        if (data.changed && data.sms_sent) toast.info('تم إرسال SMS للعميل');
        setSelectedBooking(null);
        fetchBookings(selectedDate);
        return true;
      }
      toast.error(data.message || 'فشل تحديث الموعد');
      return false;
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ في تحديث الموعد');
      return false;
    }
  };

  const openBookingDetails = async (booking) => {
    setSelectedBooking(booking);
    setEditTime(booking.time);
    setEditTimes([booking.time]);
    setLoadingEditTimes(true);
    try {
      const params = {
        date: booking.date,
        exclude_booking_id: booking.id,
      };
      if (booking.reservation_type_id) {
        params.reservation_type_id = booking.reservation_type_id;
      }
      const { data } = await api.get('/backoffice/admin-available-times', { params });
      const times = data.success ? [...(data.times || [])] : [];
      if (booking.time && !times.includes(booking.time)) {
        const idx = times.findIndex(t => t > booking.time);
        if (idx === -1) times.push(booking.time);
        else times.splice(idx, 0, booking.time);
      }
      setEditTimes(times);
    } catch {
      setEditTimes([booking.time]);
    } finally {
      setLoadingEditTimes(false);
    }
  };

  const handleUpdateBookingTime = async () => {
    if (!selectedBooking || !editTime) return;
    if (editTime === selectedBooking.time) {
      setSelectedBooking(null);
      return;
    }
    setEditSaving(true);
    try {
      await doRescheduleBooking(selectedBooking, editTime);
    } finally {
      setEditSaving(false);
    }
  };

  // ── Called from detail modal "إلغاء الحجز" button ────────────────────────
  const handleCancelBooking = (booking) => {
    setSelectedBooking(null);
    const isPast = new Date(`${booking.date}T${booking.time}`) < new Date();
    if (isPast) {
      doDeleteBooking(booking.id, false);
    } else {
      // Show custom confirmation (no block el since triggered from modal, not drag)
      setPendingCancel({ booking, blockEl: null, wrapEl: null });
    }
  };

  // ── Confirm / dismiss the custom cancel dialog ────────────────────────────
  const handleConfirmCancel = () => {
    if (!pendingCancel) return;
    const { booking, blockEl, wrapEl } = pendingCancel;
    setPendingCancel(null);
    // Animate block out if triggered by drag
    if (blockEl) {
      blockEl.style.transition = 'transform 0.18s ease, opacity 0.18s ease';
      blockEl.style.transform = 'translateX(-110%)';
      blockEl.style.opacity = '0';
      if (wrapEl) wrapEl.style.setProperty('--delete-progress', 1);
    }
    setTimeout(() => doDeleteBooking(booking.id, true), blockEl ? 180 : 0);
  };

  const handleDismissCancel = () => {
    if (!pendingCancel) return;
    const { blockEl, wrapEl } = pendingCancel;
    setPendingCancel(null);
    // Snap block back if triggered by drag
    if (blockEl) {
      blockEl.style.transition = 'transform 0.25s cubic-bezier(.25,.8,.25,1)';
      blockEl.style.transform = 'translateX(0) translateY(0)';
      blockEl.style.opacity = '1';
      if (wrapEl) wrapEl.style.setProperty('--delete-progress', 0);
      setTimeout(() => { if (blockEl) blockEl.style.transition = ''; }, 260);
    }
  };

  const handleConfirmMove = async () => {
    if (!pendingMove) return;
    const { booking, newTime, blockEl, wrapEl } = pendingMove;
    setPendingMove(null);
    const ok = await doRescheduleBooking(booking, newTime);
    if (!ok && blockEl) {
      blockEl.style.transition = 'transform 0.25s cubic-bezier(.25,.8,.25,1)';
      blockEl.style.transform = 'translateX(0) translateY(0)';
      blockEl.style.opacity = '1';
      if (wrapEl) wrapEl.style.setProperty('--delete-progress', 0);
      setTimeout(() => { if (blockEl) blockEl.style.transition = ''; }, 260);
    }
  };

  const handleDismissMove = () => {
    if (!pendingMove) return;
    const { blockEl, wrapEl } = pendingMove;
    setPendingMove(null);
    if (blockEl) {
      blockEl.style.transition = 'transform 0.25s cubic-bezier(.25,.8,.25,1)';
      blockEl.style.transform = 'translateX(0) translateY(0)';
      blockEl.style.opacity = '1';
      if (wrapEl) wrapEl.style.setProperty('--delete-progress', 0);
      setTimeout(() => { if (blockEl) blockEl.style.transition = ''; }, 260);
    }
  };

  const openBookingForm = async (preTime = '') => {
    setShowBookingForm(true);
    setPreSelectedTime(preTime);
    setBookingPhone('');
    setBookingName('');
    setBookingTime(preTime);
    setBookingTypeId('');
    setLoadingTimes(true);
    try {
      const [timesRes, typesRes] = await Promise.all([
        api.get('/backoffice/admin-available-times', { params: { date: formatDate(selectedDate) } }),
        reservationTypes.length > 0
          ? Promise.resolve({ data: { success: true, reservation_types: reservationTypes } })
          : api.get('/backoffice/reservation-types'),
      ]);
      const times = timesRes.data.success ? [...(timesRes.data.times || [])] : [];
      // Always include the clicked time even if not in available list
      if (preTime && !times.includes(preTime)) {
        const idx = times.findIndex(t => t > preTime);
        if (idx === -1) times.push(preTime);
        else times.splice(idx, 0, preTime);
      }
      setAvailableTimes(times);
      if (typesRes.data.success) setReservationTypes(typesRes.data.reservation_types);
    } catch { /* ignore */ }
    finally { setLoadingTimes(false); }
  };

  const handlePhoneBlur = async () => {
    const phone = bookingPhone.trim();
    if (!phone || bookingName.trim()) return; // skip if empty or name already filled
    setLookingUpPhone(true);
    try {
      const { data } = await api.get('/backoffice/bookings/by-phone', { params: { phone } });
      if (data.success && data.customer?.full_name) {
        setBookingName(data.customer.full_name);
      }
    } catch { /* silent — user can type manually */ }
    finally { setLookingUpPhone(false); }
  };

  const handleTypeChange = (e) => {
    const typeId = e.target.value;
    setBookingTypeId(typeId);
    setBookingTime(preSelectedTime || '');
    if (!typeId) return;
    setLoadingTimes(true);
    const params = { date: formatDate(selectedDate), reservation_type_id: typeId };
    api.get('/backoffice/admin-available-times', { params })
      .then(res => {
        if (res.data.success) {
          const times = [...(res.data.times || [])];
          if (preSelectedTime && !times.includes(preSelectedTime)) {
            const idx = times.findIndex(t => t > preSelectedTime);
            if (idx === -1) times.push(preSelectedTime);
            else times.splice(idx, 0, preSelectedTime);
          }
          setAvailableTimes(times);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingTimes(false));
  };

  const handleCreateBooking = async (e) => {
    e.preventDefault();
    if (!bookingPhone || !bookingName.trim() || !bookingTime || !bookingTypeId) {
      return toast.error('جميع البيانات مطلوبة (بما في ذلك اسم العميل)');
    }
    setBookingSaving(true);
    try {
      const { data } = await api.post('/backoffice/bookings', {
        phone: bookingPhone,
        full_name: bookingName.trim(),
        date: formatDate(selectedDate),
        time: bookingTime,
        reservation_type_id: parseInt(bookingTypeId),
      });
      if (data.success) {
        toast.success(data.message);
        setShowBookingForm(false);
        fetchBookings(selectedDate);
        if (data.not_in_allowed_list) setAllowedPrompt({ phone: bookingPhone, name: bookingName.trim() });
      } else toast.error(data.message);
    } catch (err) { toast.error(err.response?.data?.message || 'حدث خطأ'); }
    finally { setBookingSaving(false); }
  };

  const handleAddToAllowedList = async () => {
    if (!allowedPrompt) return;
    setAddingToList(true);
    try {
      const res = await api.post('/backoffice/allowed-clients', {
        phone: allowedPrompt.phone, name: allowedPrompt.name,
      });
      if (res.data.success) toast.success('تم إضافة العميل إلى القائمة المسموح بها');
      else toast.error(res.data.message || 'فشل إضافة العميل');
    } catch { toast.error('حدث خطأ في إضافة العميل'); }
    finally { setAddingToList(false); setAllowedPrompt(null); }
  };

  const openBulkSmsDialog = () => {
    if (smsBookings.length === 0) return toast.error(isToday ? 'لا توجد حجوزات قادمة اليوم' : 'لا توجد حجوزات لإرسال رسائل لها');
    setBulkSmsMessage(buildDefaultBulkSmsMessage(account?.title));
    setShowBulkSms(true);
  };

  const handleBulkSms = async () => {
    const message = bulkSmsMessage.trim();
    if (!message) return toast.error('يرجى كتابة نص الرسالة');
    const confirmText = isToday
      ? `إرسال SMS لـ ${smsBookings.length} عميل (حجوزات قادمة من الآن)؟`
      : `إرسال SMS لـ ${smsBookings.length} عميل في ${formatDateArabic(selectedDate)}؟`;
    if (!window.confirm(confirmText)) return;
    setBulkSmsSending(true);
    try {
      const { data } = await api.post('/backoffice/bookings/bulk-sms', {
        date: selectedDateStr, message, upcoming_only: isToday,
      });
      if (data.success) {
        toast.success(data.message || `تم إرسال ${data.sent} رسالة`);
        setShowBulkSms(false);
        setBulkSmsMessage('');
      } else toast.error(data.message || 'فشل الإرسال');
    } catch (err) { toast.error(err.response?.data?.message || 'حدث خطأ في إرسال الرسائل'); }
    finally { setBulkSmsSending(false); }
  };

  // ── Timeline interaction ───────────────────────────────────────────────────
  const handleEventsClick = (e) => {
    if (e.target.closest('.booking-block-wrap')) return;
    const eventsEl = eventsRef.current;
    if (!eventsEl) return;
    const rect = eventsEl.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const totalMinutes = Math.round(y / MINUTE_HEIGHT / 5) * 5;
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    if (h >= 0 && h < 24) {
      const timeStr = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      openBookingForm(timeStr);
    }
  };

  const handleEventsMouseMove = (e) => {
    const eventsEl = eventsRef.current;
    if (!eventsEl) return;
    const rect = eventsEl.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const totalMinutes = Math.round(y / MINUTE_HEIGHT / 5) * 5;
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    if (h >= 0 && h < 24) {
      setHoverTime(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
      eventsEl.style.setProperty('--hover-y', `${y}px`);
    }
  };

  const handleEventsMouseLeave = () => {
    setHoverTime('');
    if (eventsRef.current) eventsRef.current.style.removeProperty('--hover-y');
  };

  // ── Drag: swipe left to cancel, vertical drag to move time ───────────────
  const startDrag = (e, bookingId) => {
    e.stopPropagation();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const booking = bookingsRef.current.find(b => b.id === bookingId);
    const originTop = booking ? timeToMinutes(booking.time) * MINUTE_HEIGHT : 0;
    dragRef.current = {
      active: true,
      id: bookingId,
      startX: clientX,
      startY: clientY,
      dx: 0,
      dy: 0,
      axis: null,
      originTop,
    };
    dragDidMoveRef.current = false;

    const getEl = () => blockElsRef.current[bookingId];

    const snapBack = (el) => {
      el.style.transition = 'transform 0.25s cubic-bezier(.25,.8,.25,1), opacity 0.25s ease';
      el.style.transform = 'translateX(0) translateY(0)';
      el.style.opacity = '1';
      if (el.parentElement) el.parentElement.style.setProperty('--delete-progress', 0);
      setTimeout(() => { if (el) el.style.transition = ''; }, 260);
    };

    const onMove = (ev) => {
      if (!dragRef.current.active) return;
      const x = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const y = ev.touches ? ev.touches[0].clientY : ev.clientY;
      const dx = x - dragRef.current.startX;
      const dy = y - dragRef.current.startY;
      dragRef.current.dx = dx;
      dragRef.current.dy = dy;

      if (!dragRef.current.axis) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        dragRef.current.axis = Math.abs(dy) >= Math.abs(dx) ? 'y' : 'x';
        dragDidMoveRef.current = true;
      }

      const el = getEl();
      if (!el) return;
      el.style.transition = 'none';

      if (dragRef.current.axis === 'x') {
        const cancelDx = Math.min(0, dx);
        dragRef.current.dx = cancelDx;
        el.style.transform = `translateX(${cancelDx}px)`;
        const progress = Math.min(1, Math.abs(cancelDx) / DRAG_THRESHOLD);
        if (el.parentElement) el.parentElement.style.setProperty('--delete-progress', progress);
      } else {
        el.style.transform = `translateY(${dy}px)`;
        if (el.parentElement) el.parentElement.style.setProperty('--delete-progress', 0);
      }
    };

    const onEnd = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onEnd);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);

      const { dx, dy, id, axis, originTop } = dragRef.current;
      dragRef.current.active = false;
      const el = getEl();
      if (!el) return;

      const booking = bookingsRef.current.find(b => b.id === id);
      if (!booking) { snapBack(el); return; }

      if (axis === 'x' && dx < -DRAG_THRESHOLD) {
        const isPast = new Date(`${booking.date}T${booking.time}`) < new Date();
        if (isPast) {
          el.style.transition = 'transform 0.18s ease, opacity 0.18s ease';
          el.style.transform = 'translateX(-110%)';
          el.style.opacity = '0';
          if (el.parentElement) el.parentElement.style.setProperty('--delete-progress', 1);
          setTimeout(() => doDeleteBooking(booking.id, false), 180);
        } else {
          setPendingCancelRef.current({ booking, blockEl: el, wrapEl: el.parentElement });
        }
        return;
      }

      if (axis === 'y' && Math.abs(dy) >= MOVE_THRESHOLD) {
        const newTime = minutesToHHMM((originTop + dy) / MINUTE_HEIGHT);
        if (newTime !== booking.time) {
          setPendingMoveRef.current({
            booking,
            newTime,
            blockEl: el,
            wrapEl: el.parentElement,
          });
          return;
        }
      }

      snapBack(el);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onEnd);
    document.addEventListener('touchmove', onMove, { passive: true });
    document.addEventListener('touchend', onEnd);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="dashboard">

      {/* ── Top Bar ──────────────────────────────────────────────────── */}
      <div className="dash-topbar">
        <div className="dash-topbar-info">
          <h1 className="dash-title">{account?.title}</h1>
          <div className="dash-date-ar">{formatDateArabic(selectedDate)}</div>
        </div>
        <div className="dash-topbar-controls">
          <div className="date-nav">
            <button className="nav-btn" onClick={() => goDay(-1)} title="اليوم السابق">&#9664;</button>
            <input
              type="date"
              className="date-picker-input"
              value={selectedDateStr}
              onChange={e => handleDateInput(e.target.value)}
            />
            <button
              className={`today-btn${isToday ? ' active' : ''}`}
              onClick={goToday}
            >
              اليوم
            </button>
            <button className="nav-btn" onClick={() => goDay(1)} title="اليوم التالي">&#9654;</button>
          </div>
          <div className="dash-actions">
            <button className="add-booking-btn" onClick={() => openBookingForm()}>
              + إضافة حجز
            </button>
            {smsBookings.length > 0 && (
              <button className="bulk-sms-btn" onClick={openBulkSmsDialog}>
                📱 SMS ({smsBookings.length})
              </button>
            )}
            {isClosed ? (
              <span className="day-closed-badge">&#10003; مغلق</span>
            ) : bookings.length > 0 ? (
              <button className="close-day-btn" onClick={handleCloseDay} disabled={closing}>
                {closing ? 'جاري...' : 'إغلاق اليوم'}
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {/* ── Info bar ─────────────────────────────────────────────────── */}
      <div className="dash-info-bar">
        {loading ? (
          <span className="info-bar-hint">جاري التحميل...</span>
        ) : bookings.length > 0 ? (
          <span className="booking-count-pill">{bookings.length} حجز</span>
        ) : (
          <span className="info-bar-hint">لا توجد حجوزات — انقر على أي وقت لإضافة حجز جديد</span>
        )}
        {hoverTime && !loading && (
          <span className="hover-time-display">{hoverTime}</span>
        )}
      </div>

      {/* ── Timeline ─────────────────────────────────────────────────── */}
      <div className="timeline-outer" ref={timelineRef}>
        {loading ? (
          <div className="timeline-loading">
            <div className="loading-spinner" />
            <span>جاري تحميل الحجوزات...</span>
          </div>
        ) : (
          <div className="timeline-inner" dir="ltr">

            {/* Hour label column */}
            <div className="timeline-labels">
              {HOURS.map(h => (
                <div key={h} className="timeline-label" style={{ height: HOUR_HEIGHT }}>
                  {formatHourLabel(h)}
                </div>
              ))}
            </div>

            {/* Events column */}
            <div
              className="timeline-events"
              ref={eventsRef}
              onClick={handleEventsClick}
              onMouseMove={handleEventsMouseMove}
              onMouseLeave={handleEventsMouseLeave}
              style={{ height: 24 * HOUR_HEIGHT }}
            >
              {/* Off-hours shading (full-day gray for non-working days; before/breaks/after for working days) */}
              {calendarSettings && (() => {
                const dayName = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][selectedDate.getDay()];
                const isWorkingDay = (calendarSettings.working_days || []).includes(dayName);
                const fullDayHeight = 24 * 60 * MINUTE_HEIGHT;

                // Non-working day or manually closed → shade entire timeline
                if (!isWorkingDay || isClosed) {
                  return (
                    <div
                      className="offhours-overlay"
                      style={{ top: 0, height: fullDayHeight }}
                    />
                  );
                }

                // Working day → shade before start, break bands, after end
                const dayOverride = (calendarSettings.day_hours || {})[dayName];
                const startMin = timeToMinutes(dayOverride ? dayOverride.start : (calendarSettings.start_hour || '00:00'));
                const endMin   = timeToMinutes(dayOverride ? dayOverride.end   : (calendarSettings.end_hour   || '24:00'));

                const offHoursMap = calendarSettings.off_hours || {};
                const globalBreaks = calendarSettings.global_breaks || [];
                const breakStrings = dayName in offHoursMap ? offHoursMap[dayName] : globalBreaks;
                const breaks = breakStrings
                  .map(s => {
                    const [a, b] = s.split('-');
                    return { start: timeToMinutes(a?.trim()), end: timeToMinutes(b?.trim()) };
                  })
                  .filter(br => br.start != null && br.end != null && br.end > br.start);

                return (
                  <>
                    {startMin > 0 && (
                      <div
                        className="offhours-overlay"
                        style={{ top: 0, height: startMin * MINUTE_HEIGHT }}
                      />
                    )}
                    {breaks.map((br, i) => (
                      <div
                        key={`break-${i}`}
                        className="offhours-overlay offhours-break"
                        style={{ top: br.start * MINUTE_HEIGHT, height: (br.end - br.start) * MINUTE_HEIGHT }}
                      />
                    ))}
                    {endMin < 24 * 60 && (
                      <div
                        className="offhours-overlay"
                        style={{ top: endMin * MINUTE_HEIGHT, height: (24 * 60 - endMin) * MINUTE_HEIGHT }}
                      />
                    )}
                  </>
                );
              })()}

              {/* Grid: hour lines */}
              {HOURS.map(h => (
                <div key={h} className="hour-line" style={{ top: h * HOUR_HEIGHT }} />
              ))}
              {/* Grid: half-hour dashed lines */}
              {HOURS.map(h => (
                <div key={`hh-${h}`} className="half-line" style={{ top: h * HOUR_HEIGHT + HOUR_HEIGHT / 2 }} />
              ))}

              {/* Current time indicator (today only) */}
              {isToday && (
                <div className="now-indicator" style={{ top: nowMinutes * MINUTE_HEIGHT }}>
                  <div className="now-dot" />
                  <div className="now-line" />
                </div>
              )}

              {/* Booking blocks */}
              {bookings.map(b => {
                const topPx = timeToMinutes(b.time) * MINUTE_HEIGHT;
                const dur = getBookingDuration(b);
                const heightPx = Math.max(dur * MINUTE_HEIGHT, 22);
                const endTime = addMinutes(b.time, dur);
                return (
                  <div
                    key={b.id}
                    className="booking-block-wrap"
                    style={{ top: topPx, height: heightPx }}
                  >
                    {/* Red delete layer revealed on swipe */}
                    <div className="booking-delete-bg">
                      <span className="delete-icon">🗑</span>
                    </div>
                    {/* The draggable block */}
                    <div
                      className="booking-block"
                      ref={el => { blockElsRef.current[b.id] = el; }}
                      onMouseDown={e => startDrag(e, b.id)}
                      onTouchStart={e => startDrag(e, b.id)}
                      onClick={e => {
                        e.stopPropagation();
                        if (dragDidMoveRef.current) { dragDidMoveRef.current = false; return; }
                        openBookingDetails(b);
                      }}
                      title={`${b.full_name || b.phone} — ${b.time} → ${endTime}`}
                    >
                      <div className="block-time">{b.time} – {endTime}</div>
                      <div className="block-name">{b.full_name || b.phone}</div>
                      {b.booking_type && <div className="block-type">{b.booking_type}</div>}
                    </div>
                  </div>
                );
              })}

            </div>
          </div>
        )}
      </div>

      {/* ── Booking Detail Modal ──────────────────────────────────────── */}
      {selectedBooking && (
        <div className="modal-overlay" onClick={() => setSelectedBooking(null)}>
          <div className="booking-detail-dialog" onClick={e => e.stopPropagation()}>
            <div className="bd-header">
              <h3>تفاصيل الحجز</h3>
              <button className="bd-close-x" onClick={() => setSelectedBooking(null)}>✕</button>
            </div>
            <div className="bd-body">
              <div className="bd-row">
                <span className="bd-label">الاسم الكامل</span>
                <strong className="bd-value">{selectedBooking.full_name || '—'}</strong>
              </div>
              <div className="bd-row">
                <span className="bd-label">رقم الهاتف</span>
                <span className="bd-value" dir="ltr">{selectedBooking.phone || '—'}</span>
              </div>
              <div className="bd-row">
                <span className="bd-label">نوع الحجز</span>
                <span className="bd-value">
                  <span className="booking-type-badge">{selectedBooking.booking_type || '—'}</span>
                </span>
              </div>
              <div className="bd-row">
                <span className="bd-label">من</span>
                <span className="bd-value bd-time" dir="ltr">
                  {loadingEditTimes ? (
                    'جاري التحميل...'
                  ) : (
                    <select
                      className="bd-time-select"
                      value={editTime}
                      onChange={e => setEditTime(e.target.value)}
                      dir="ltr"
                    >
                      {editTimes.map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  )}
                </span>
              </div>
              <div className="bd-row">
                <span className="bd-label">إلى</span>
                <span className="bd-value bd-time" dir="ltr">
                  {addMinutes(editTime || selectedBooking.time, getBookingDuration(selectedBooking))}
                </span>
              </div>
              <div className="bd-row">
                <span className="bd-label">تاريخ الحجز</span>
                <span className="bd-value">{formatCreatedAt(selectedBooking.created_at)}</span>
              </div>
              {(selectedBooking.reschedule_count || 0) > 0 && (
                <div className="bd-row">
                  <span className="bd-label">مرات التأجيل</span>
                  <span className="bd-value">{selectedBooking.reschedule_count}</span>
                </div>
              )}
            </div>
            <div className="bd-footer">
              <button
                className="bd-update-btn"
                onClick={handleUpdateBookingTime}
                disabled={editSaving || loadingEditTimes || !editTime || editTime === selectedBooking.time}
              >
                {editSaving ? 'جاري التحديث...' : 'تحديث الموعد'}
              </button>
              <button
                className="bd-cancel-btn"
                onClick={() => handleCancelBooking(selectedBooking)}
              >
                إلغاء الحجز
              </button>
              <button className="bd-dismiss-btn" onClick={() => setSelectedBooking(null)}>
                إغلاق
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── New Booking Form ──────────────────────────────────────────── */}
      {showBookingForm && (
        <div className="modal-overlay" onClick={() => setShowBookingForm(false)}>
          <div className="booking-form-dialog" onClick={e => e.stopPropagation()}>
            <h3>
              إضافة حجز{preSelectedTime ? ` — ${preSelectedTime}` : ''}
              <span className="form-date-sub">{formatDateArabic(selectedDate)}</span>
            </h3>
            {loadingTimes ? (
              <div className="bookings-loading">جاري التحميل...</div>
            ) : (availableTimes.length === 0 && !bookingTime) ? (
              <div className="bookings-empty">
                <p>لا توجد أوقات متاحة في هذا اليوم</p>
                <button className="dismiss-btn" onClick={() => setShowBookingForm(false)}>إغلاق</button>
              </div>
            ) : (
              <form onSubmit={handleCreateBooking}>
                <div className="bf-group">
                  <label>رقم الهاتف *</label>
                  <input
                    type="tel"
                    value={bookingPhone}
                    onChange={e => setBookingPhone(e.target.value)}
                    onBlur={handlePhoneBlur}
                    placeholder="05XXXXXXXX"
                    dir="ltr"
                    required
                  />
                </div>
                <div className="bf-group">
                  <label>
                    اسم العميل *
                    {lookingUpPhone && <span className="phone-lookup-spinner"> ...</span>}
                  </label>
                  <input
                    type="text"
                    value={bookingName}
                    onChange={e => setBookingName(e.target.value)}
                    placeholder={lookingUpPhone ? 'جاري البحث...' : 'أدخل اسم العميل'}
                    required
                  />
                </div>
                <div className="bf-group">
                  <label>نوع الحجز *</label>
                  <select value={bookingTypeId} onChange={handleTypeChange} required>
                    <option value="">اختر نوع الحجز</option>
                    {reservationTypes.map(rt => {
                      const dur = rt.duration_minutes;
                      let durLabel = '';
                      if (dur) {
                        const hh = Math.floor(dur / 60), mm = dur % 60;
                        durLabel = hh === 0 ? `${mm} د` : mm === 0 ? `${hh} س` : `${hh}:${String(mm).padStart(2, '0')}`;
                      }
                      return (
                        <option key={rt.id} value={rt.id}>
                          {rt.name}{rt.price ? ` (${rt.price}₪)` : ''}{durLabel ? ` — ${durLabel}` : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="bf-group">
                  <label>الوقت *</label>
                  {loadingTimes ? (
                    <div className="bookings-loading" style={{ padding: '0.5rem 0' }}>جاري التحميل...</div>
                  ) : (
                    <div className="time-slots-grid">
                      {availableTimes.map(t => (
                        <button
                          key={t}
                          type="button"
                          className={`time-slot-chip${bookingTime === t ? ' active' : ''}${t === preSelectedTime && bookingTime !== t ? ' pre-hint' : ''}`}
                          onClick={() => setBookingTime(t)}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="bf-actions">
                  <button type="submit" className="save-btn" disabled={bookingSaving}>
                    {bookingSaving ? 'جاري الحفظ...' : 'إنشاء الحجز'}
                  </button>
                  <button type="button" className="dismiss-btn" onClick={() => setShowBookingForm(false)}>
                    إلغاء
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ── Bulk SMS Dialog ───────────────────────────────────────────── */}
      {showBulkSms && (
        <div className="modal-overlay" onClick={() => !bulkSmsSending && setShowBulkSms(false)}>
          <div className="booking-form-dialog bulk-sms-dialog" onClick={e => e.stopPropagation()}>
            <h3>إرسال SMS {isToday ? 'للحجوزات القادمة اليوم' : 'لعملاء اليوم'}</h3>
            <p className="bulk-sms-hint">
              {smsBookings.length} عميل — {formatDateArabic(selectedDate)}
              {isToday && ' (من الآن حتى نهاية اليوم)'}
            </p>
            <p className="bulk-sms-vars">متغيرات: {'{time}'} {'{salon}'} {'{name}'}</p>
            <div className="bf-group">
              <label>نص الرسالة</label>
              <textarea
                rows={4}
                value={bulkSmsMessage}
                onChange={e => setBulkSmsMessage(e.target.value)}
              />
            </div>
            <div className="bf-actions">
              <button
                type="button"
                className="save-btn"
                onClick={handleBulkSms}
                disabled={bulkSmsSending || !bulkSmsMessage.trim()}
              >
                {bulkSmsSending ? 'جاري الإرسال...' : `إرسال لـ ${smsBookings.length} عميل`}
              </button>
              <button
                type="button"
                className="dismiss-btn"
                onClick={() => setShowBulkSms(false)}
                disabled={bulkSmsSending}
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Custom Cancel Confirmation Dialog ────────────────────────── */}
      {pendingCancel && (
        <div className="modal-overlay cancel-confirm-overlay" onClick={handleDismissCancel}>
          <div className="cancel-confirm-dialog" onClick={e => e.stopPropagation()}>
            <div className="cancel-confirm-icon">🗑</div>
            <h3>إلغاء الحجز</h3>
            <p className="cancel-confirm-desc">
              هل أنت متأكد من إلغاء حجز{' '}
              <strong>{pendingCancel.booking.full_name || pendingCancel.booking.phone}</strong>
              {' '}الساعة <span dir="ltr">{pendingCancel.booking.time}</span>؟
            </p>
            <p className="cancel-confirm-sms-note">📱 سيتم إرسال رسالة SMS للعميل</p>
            <div className="cancel-confirm-actions">
              <button className="cancel-confirm-yes" onClick={handleConfirmCancel}>
                نعم، إلغاء الحجز
              </button>
              <button className="cancel-confirm-no" onClick={handleDismissCancel}>
                لا، تراجع
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Custom Move Confirmation Dialog ───────────────────────────── */}
      {pendingMove && (
        <div className="modal-overlay cancel-confirm-overlay" onClick={handleDismissMove}>
          <div className="cancel-confirm-dialog move-confirm-dialog" onClick={e => e.stopPropagation()}>
            <div className="cancel-confirm-icon move-confirm-icon">⏱</div>
            <h3>نقل الحجز</h3>
            <p className="cancel-confirm-desc">
              هل أنت متأكد من نقل حجز{' '}
              <strong>{pendingMove.booking.full_name || pendingMove.booking.phone}</strong>
              {' '}إلى الساعة <span dir="ltr">{pendingMove.newTime}</span>؟
            </p>
            <p className="cancel-confirm-sms-note">📱 سيتم إرسال رسالة SMS للعميل بالموعد الجديد</p>
            <div className="cancel-confirm-actions">
              <button className="move-confirm-yes" onClick={handleConfirmMove}>
                نعم، تحديث
              </button>
              <button className="cancel-confirm-no" onClick={handleDismissMove}>
                لا، تراجع
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add to Allowed List Prompt ────────────────────────────────── */}
      {allowedPrompt && (
        <div className="allowed-prompt-overlay">
          <div className="allowed-prompt-dialog">
            <div className="allowed-prompt-icon">&#128100;</div>
            <h3>عميل غير مسجل في القائمة</h3>
            <p className="allowed-prompt-desc">
              العميل <strong>{allowedPrompt.name}</strong> برقم{' '}
              <span dir="ltr" className="allowed-prompt-phone">{allowedPrompt.phone}</span>{' '}
              غير موجود في قائمة العملاء المسموح لهم.
            </p>
            <p className="allowed-prompt-question">هل تريد إضافته إلى القائمة المسموح بها؟</p>
            <div className="allowed-prompt-actions">
              <button
                className="allowed-prompt-add"
                onClick={handleAddToAllowedList}
                disabled={addingToList}
              >
                {addingToList ? 'جاري الإضافة...' : 'نعم، أضف إلى القائمة'}
              </button>
              <button
                className="allowed-prompt-skip"
                onClick={() => setAllowedPrompt(null)}
                disabled={addingToList}
              >
                لا، شكراً
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
